import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import {
  PAYMENT_HISTORY_PAGE_SIZE,
  type DepositBonusOption,
  type DepositBonusesResponse,
  type PaymentBank,
  type PaymentProvider,
  type PaymentRequestRow,
  type PaymentWalletType,
  type WalletPaymentResponse,
  filterProvidersBySearch,
  findProviderById,
  parseDepositBonusesResponse,
  sortProvidersByPosition,
} from '@/domain/payments';
import {
  paymentFieldSet,
  isRedepositProvider,
  resolvePaymentProvider,
  type PaymentProviderConfig,
} from '@/domain/payment-providers';
import { getAccount, getUserSettings, updateUserCredits } from './auth';
import { api } from './client';
import { endpoints } from './endpoints';
import { queryKeys } from './query-keys';

export interface ProviderListRequest {
  AccountGuid: string;
  MachineGuid: string;
  CountryId?: number | null;
  Currency?: string | null;
}

export interface PaymentHistoryRequest {
  TableFilters: {
    MachineGuid: string;
    DateFrom?: string | null;
    DateTo?: string | null;
  };
  Paging: {
    PageNumber: number;
    NumberOfResultsToDisplay: number;
  };
  Sorting: { SortBy: string; Direction: string };
}

export interface PaymentHistoryResponse {
  Data?: PaymentRequestRow[] | null;
  Filter?: {
    PageNumber?: number;
    NumberOfPages?: number;
    NumberOfResults?: number;
  } | null;
}

export interface DepositBonusesRequest {
  MachineGuid: string;
  ProviderId: number;
  Language?: string | null;
}

function providerListBody(): ProviderListRequest {
  const account = getAccount();
  const user = getUserSettings();
  return {
    AccountGuid: account?.Guid ?? '',
    MachineGuid: user?.Guid?.trim() ?? '',
    CountryId: user?.CountryId ?? null,
    Currency: user?.Currency ?? user?.CurrCode ?? user?.CurrencyCode ?? null,
  };
}

function historyBody(page = 1): PaymentHistoryRequest {
  const user = getUserSettings();
  return {
    TableFilters: { MachineGuid: user?.Guid?.trim() ?? '' },
    Paging: {
      PageNumber: page,
      NumberOfResultsToDisplay: PAYMENT_HISTORY_PAGE_SIZE,
    },
    Sorting: { SortBy: 'Date', Direction: 'desc' },
  };
}

async function refreshPlayerCredits(): Promise<void> {
  const machineGuid = getUserSettings()?.Guid?.trim();
  if (!machineGuid) return;
  try {
    const credits = await api.post(
      endpoints.player.refreshCredits.host,
      endpoints.player.refreshCredits.path,
      { Value: machineGuid },
    );
    if (credits && typeof credits === 'object') {
      updateUserCredits(credits as Parameters<typeof updateUserCredits>[0]);
    }
  } catch {
    // Credits refresh is best-effort after payment actions.
  }
}

export function listDepositProviders(signal?: AbortSignal): Promise<PaymentProvider[]> {
  return api.post<PaymentProvider[]>(
    endpoints.providers.deposits.host,
    endpoints.providers.deposits.path,
    providerListBody() as unknown as Record<string, unknown>,
    signal,
  );
}

export function listWithdrawalProviders(signal?: AbortSignal): Promise<PaymentProvider[]> {
  return api.post<PaymentProvider[]>(
    endpoints.providers.withdrawals.host,
    endpoints.providers.withdrawals.path,
    providerListBody() as unknown as Record<string, unknown>,
    signal,
  );
}

export function listDepositHistory(
  request: PaymentHistoryRequest = historyBody(),
  signal?: AbortSignal,
): Promise<PaymentHistoryResponse> {
  return api.post<PaymentHistoryResponse>(
    endpoints.player.depositHistory.host,
    endpoints.player.depositHistory.path,
    request as unknown as Record<string, unknown>,
    signal,
  );
}

export function listWithdrawalHistory(
  request: PaymentHistoryRequest = historyBody(),
  signal?: AbortSignal,
): Promise<PaymentHistoryResponse> {
  return api.post<PaymentHistoryResponse>(
    endpoints.player.withdrawalHistory.host,
    endpoints.player.withdrawalHistory.path,
    request as unknown as Record<string, unknown>,
    signal,
  );
}

export interface DepositBonusesListResult {
  bonuses: DepositBonusOption[];
  message: string | null;
  success: boolean;
}

export async function listDepositBonuses(
  request: DepositBonusesRequest,
  signal?: AbortSignal,
): Promise<DepositBonusesListResult> {
  const raw = await api.post<DepositBonusesResponse | DepositBonusOption[]>(
    endpoints.player.depositBonuses.host,
    endpoints.player.depositBonuses.path,
    request as unknown as Record<string, unknown>,
    signal,
  );
  return parseDepositBonusesResponse(raw);
}

export function cancelDeposit(encId: string, signal?: AbortSignal): Promise<{ Success?: boolean; Error?: string }> {
  return api.post(
    endpoints.player.depositCancel.host,
    endpoints.player.depositCancel.path,
    { ItemId: encId },
    signal,
  );
}

export function listUsersBanks(
  accountProviderId: number,
  signal?: AbortSignal,
): Promise<PaymentBank[]> {
  return api.post<PaymentBank[]>(
    endpoints.providers.banks.host,
    endpoints.providers.banks.path,
    { Value: accountProviderId },
    signal,
  );
}

export async function listWalletBanks(
  walletSlug: string,
  providerId: number,
  signal?: AbortSignal,
): Promise<PaymentBank[]> {
  const user = getUserSettings();
  const response = await api.post<{ Success?: boolean; Banks?: PaymentBank[] }>(
    endpoints.wallet.banks(walletSlug).host,
    endpoints.wallet.banks(walletSlug).path,
    { WalletId: user?.WalletId ?? '', ProviderId: providerId },
    signal,
  );
  return response?.Banks ?? [];
}

export async function listWalletTypes(
  walletSlug: string,
  providerId: number,
  signal?: AbortSignal,
): Promise<PaymentWalletType[]> {
  const user = getUserSettings();
  const response = await api.post<{ Success?: boolean; WalletTypes?: PaymentWalletType[] }>(
    endpoints.wallet.walletTypes(walletSlug).host,
    endpoints.wallet.walletTypes(walletSlug).path,
    { WalletId: user?.WalletId ?? '', ProviderId: providerId },
    signal,
  );
  return response?.WalletTypes ?? [];
}

export function listPaymentAccounts(
  providerId: number,
  signal?: AbortSignal,
): Promise<{ MachineProviderId: number; AccountNumber: string }[]> {
  const user = getUserSettings();
  return api.post(
    endpoints.providers.paymentAccounts.host,
    endpoints.providers.paymentAccounts.path,
    { MachineGuid: user?.Guid?.trim() ?? '', ProviderId: providerId },
    signal,
  );
}

export interface SubmitPaymentInput {
  mode: 'deposit' | 'withdrawal';
  provider: PaymentProvider;
  amount: number;
  accountBonusId?: string | null;
  fields?: Record<string, string>;
}

function commonPaymentPayload(
  provider: PaymentProvider,
  config: PaymentProviderConfig,
  amount: number,
  fields: Record<string, string>,
  accountBonusId?: string | null,
): Record<string, unknown> {
  const user = getUserSettings();
  const account = getAccount();
  const redirectUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}/profile/deposits`
      : '';

  const payload: Record<string, unknown> = {
    WalletId: user?.WalletId ?? '',
    ProviderId: provider.Id,
    Amount: amount,
    MachineGuid: user?.Guid?.trim() ?? '',
    Telephone: user?.Telephone ?? '',
    Email: user?.Email ?? '',
    Lang: user?.Language ?? account?.Language?.Short ?? 'en',
    CurrencyCode: user?.CurrencyCode ?? user?.CurrCode ?? '',
    CurrencyNumber: user?.CurrencyId ?? '',
    DefaultCurrency: provider.DefaultCurrency ?? '',
    FullName: user?.Name ?? '',
    Username: user?.Username ?? '',
    RedirectUrl: redirectUrl,
    ReturnUrl: redirectUrl,
    DefaultProviderCurrencyValue: provider.DefaultProviderCurrencyValue ?? '',
    DefaultMachineCurrencyValue: provider.DefaultMachineCurrencyValue ?? '',
    ConvertCurrency: provider.ConvertCurrency ?? false,
    DefaultProviderCurrencyCode: provider.DefaultProviderCurrencyCode ?? '',
    ...config.extras,
  };

  if (accountBonusId) payload.AccountBonusId = accountBonusId;

  if (fields.bank) {
    payload.BankId = fields.bank;
    payload.Bank = fields.bank;
  }
  if (fields.iban) payload.IBAN = fields.iban;
  if (fields.accountHolder) {
    payload.AccountName = fields.accountHolder;
    payload.FullName = fields.accountHolder;
  }
  if (fields.accountNumber) {
    payload.AccountNumber = fields.accountNumber;
    payload.AccountNo = fields.accountNumber;
  }
  if (fields.cryptoAddress) {
    payload.CryptoAddress = fields.cryptoAddress;
  }
  if (fields.cryptocurrency) payload.Cryptocurrency = fields.cryptocurrency;
  if (fields.network) payload.Network = fields.network;
  if (fields.phone) {
    payload.SenderPhone = fields.phone;
    payload.ReceiverPhone = fields.phone;
  }
  if (fields.voucher) payload.VoucherPIN = fields.voucher;
  if (fields.walletType) {
    payload.CryptoCurrencyCode = fields.walletType;
    payload.WalletTypeId = fields.walletType;
  }
  if (fields.machineProviderId) {
    payload.MachineProviderId = fields.machineProviderId;
  }

  return payload;
}

export async function submitPayment(
  input: SubmitPaymentInput,
  signal?: AbortSignal,
): Promise<WalletPaymentResponse> {
  const redeposit = isRedepositProvider(input.provider);
  const config = resolvePaymentProvider(input.provider.Name, { isRedeposit: redeposit });

  // Redeposit is deposit-only (legacy opens DepositRequest from the withdrawal list).
  if (redeposit && input.mode === 'withdrawal') {
    return { Success: false, Error: 'WithdrawalNotSupported' };
  }

  const endpoint =
    input.mode === 'deposit'
      ? endpoints.wallet.deposit(config.walletSlug)
      : endpoints.wallet.withdrawal(config.walletSlug);

  const user = getUserSettings();
  const payload =
    config.walletSlug === 'redeposit'
      ? {
          WalletId: user?.WalletId ?? '',
          ProviderId: input.provider.Id,
          Amount: input.amount,
          ...(input.accountBonusId ? { AccountBonusId: input.accountBonusId } : {}),
        }
      : commonPaymentPayload(
          input.provider,
          config,
          input.amount,
          input.fields ?? {},
          input.accountBonusId,
        );

  const response = await api.post<WalletPaymentResponse>(
    endpoint.host,
    endpoint.path,
    payload,
    signal,
  );

  if (response?.Success) {
    await refreshPlayerCredits();
  }

  return response ?? { Success: false, Error: 'Empty response' };
}

export interface UsePaymentProvidersResult {
  providers: PaymentProvider[];
  filtered: PaymentProvider[];
  search: string;
  setSearch: (value: string) => void;
  isLoading: boolean;
  error: Error | null;
  currency: string;
  findById: (providerId: string) => PaymentProvider | undefined;
}

function usePaymentProvidersList(
  mode: 'deposit' | 'withdrawal',
  enabled = true,
): UsePaymentProvidersResult {
  const [search, setSearch] = useState('');
  const user = getUserSettings();
  const machineGuid = user?.Guid?.trim() ?? '';
  const currency = (user?.Currency ?? user?.CurrCode ?? user?.CurrencyCode ?? '').trim();

  const query = useQuery({
    queryKey:
      mode === 'deposit'
        ? queryKeys.player.depositProviders({ machineGuid })
        : queryKeys.player.withdrawalProviders({ machineGuid }),
    enabled: enabled && Boolean(machineGuid),
    queryFn: ({ signal }) =>
      mode === 'deposit' ? listDepositProviders(signal) : listWithdrawalProviders(signal),
  });

  const providers = useMemo(() => {
    const list = Array.isArray(query.data) ? query.data : [];
    return sortProvidersByPosition(list);
  }, [query.data]);

  const filtered = useMemo(
    () => filterProvidersBySearch(providers, search),
    [providers, search],
  );

  const findById = useCallback(
    (providerId: string) => findProviderById(providers, providerId),
    [providers],
  );

  return {
    providers,
    filtered,
    search,
    setSearch,
    isLoading: query.isLoading,
    error: query.error instanceof Error ? query.error : null,
    currency,
    findById,
  };
}

export function useDepositProviders(enabled = true): UsePaymentProvidersResult {
  return usePaymentProvidersList('deposit', enabled);
}

export function useWithdrawalProviders(enabled = true): UsePaymentProvidersResult {
  return usePaymentProvidersList('withdrawal', enabled);
}

export interface UsePaymentHistoryResult {
  rows: PaymentRequestRow[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  cancel: (encId: string) => Promise<void>;
  isCancelling: boolean;
  cancelError: Error | null;
}

function usePaymentHistory(
  mode: 'deposit' | 'withdrawal',
  enabled = true,
): UsePaymentHistoryResult {
  const queryClient = useQueryClient();
  const user = getUserSettings();
  const machineGuid = user?.Guid?.trim() ?? '';

  const query = useQuery({
    queryKey:
      mode === 'deposit'
        ? queryKeys.player.depositHistory({ machineGuid })
        : queryKeys.player.withdrawalHistory({ machineGuid }),
    enabled: enabled && Boolean(machineGuid),
    queryFn: ({ signal }) =>
      mode === 'deposit'
        ? listDepositHistory(historyBody(1), signal)
        : listWithdrawalHistory(historyBody(1), signal),
  });

  const cancelMutation = useMutation({
    mutationFn: (encId: string) => cancelDeposit(encId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.player.depositHistory({ machineGuid }),
      });
      await refreshPlayerCredits();
    },
  });

  return {
    rows: query.data?.Data ?? [],
    isLoading: query.isLoading,
    error: query.error instanceof Error ? query.error : null,
    refetch: () => {
      void query.refetch();
    },
    cancel: async (encId: string) => {
      await cancelMutation.mutateAsync(encId);
    },
    isCancelling: cancelMutation.isPending,
    cancelError: cancelMutation.error instanceof Error ? cancelMutation.error : null,
  };
}

export function useDepositHistory(enabled = true): UsePaymentHistoryResult {
  return usePaymentHistory('deposit', enabled);
}

export function useWithdrawalHistory(enabled = true): UsePaymentHistoryResult {
  return usePaymentHistory('withdrawal', enabled);
}

export interface UseDepositBonusesResult {
  bonuses: DepositBonusOption[];
  /** Eligibility / empty message key from UsersAPI when Success is false. */
  message: string | null;
  isLoading: boolean;
  error: Error | null;
}

export function useDepositBonuses(
  providerId: number | null,
  enabled = true,
): UseDepositBonusesResult {
  const user = getUserSettings();
  const machineGuid = user?.Guid?.trim() ?? '';
  const languageRaw = user?.Language ?? getAccount()?.Language?.Short ?? 'en';
  const language = languageRaw.trim().toUpperCase() || 'EN';

  const query = useQuery({
    queryKey: queryKeys.player.depositBonuses({ machineGuid, providerId, language }),
    enabled: enabled && Boolean(machineGuid) && providerId != null && providerId > 0,
    queryFn: ({ signal }) =>
      listDepositBonuses(
        {
          MachineGuid: machineGuid,
          ProviderId: providerId!,
          Language: language,
        },
        signal,
      ),
  });

  return {
    bonuses: query.data?.bonuses ?? [],
    message: query.data?.message ?? null,
    isLoading: query.isLoading,
    error: query.error instanceof Error ? query.error : null,
  };
}

export interface UsePaymentResourcesResult {
  banks: PaymentBank[];
  walletTypes: PaymentWalletType[];
  paymentAccounts: { MachineProviderId: number; AccountNumber: string }[];
  isLoading: boolean;
  error: Error | null;
  config: PaymentProviderConfig | null;
  fields: ReturnType<typeof paymentFieldSet>;
}

export function usePaymentResources(
  mode: 'deposit' | 'withdrawal',
  provider: PaymentProvider | null | undefined,
  enabled = true,
): UsePaymentResourcesResult {
  const config = provider
    ? resolvePaymentProvider(provider.Name, { isRedeposit: isRedepositProvider(provider) })
    : null;
  const fields = config ? paymentFieldSet(config, mode) : [];
  const providerId = provider?.Id ?? 0;
  const walletSlug = config?.walletSlug ?? '';
  const banksSource = config?.banksSource ?? 'none';
  const needWalletTypes = Boolean(config?.walletTypes);
  const needPaymentAccounts = Boolean(config?.paymentAccounts) && mode === 'withdrawal';
  const accountProviderId = provider?.AccountProviderId ?? 0;

  const banksQuery = useQuery({
    queryKey: queryKeys.player.paymentBanks({
      source: banksSource,
      walletSlug,
      providerId,
      accountProviderId,
    }),
    enabled:
      enabled &&
      Boolean(provider) &&
      ((banksSource === 'wallet' && Boolean(walletSlug)) ||
        (banksSource === 'users' && accountProviderId > 0) ||
        (banksSource === 'none' && false)),
    queryFn: ({ signal }) => {
      if (banksSource === 'wallet') return listWalletBanks(walletSlug, providerId, signal);
      if (banksSource === 'users') return listUsersBanks(accountProviderId, signal);
      return Promise.resolve([]);
    },
  });

  // Also load UsersAPI banks when withdrawal form needs bank field and no wallet banks.
  const usersBanksFallback = useQuery({
    queryKey: queryKeys.player.paymentBanks({
      source: 'users-fallback',
      accountProviderId,
      providerId,
    }),
    enabled:
      enabled &&
      mode === 'withdrawal' &&
      fields.includes('bank') &&
      banksSource === 'none' &&
      accountProviderId > 0,
    queryFn: ({ signal }) => listUsersBanks(accountProviderId, signal),
  });

  const walletTypesQuery = useQuery({
    queryKey: queryKeys.player.paymentWalletTypes({ walletSlug, providerId }),
    enabled: enabled && needWalletTypes && Boolean(walletSlug) && providerId > 0,
    queryFn: ({ signal }) => listWalletTypes(walletSlug, providerId, signal),
  });

  const accountsQuery = useQuery({
    queryKey: queryKeys.player.paymentAccounts({ providerId }),
    enabled: enabled && needPaymentAccounts && providerId > 0,
    queryFn: ({ signal }) => listPaymentAccounts(providerId, signal),
  });

  const banks = banksQuery.data?.length
    ? banksQuery.data
    : (usersBanksFallback.data ?? []);

  return {
    banks,
    walletTypes: walletTypesQuery.data ?? [],
    paymentAccounts: accountsQuery.data ?? [],
    isLoading:
      banksQuery.isLoading ||
      usersBanksFallback.isLoading ||
      walletTypesQuery.isLoading ||
      accountsQuery.isLoading,
    error:
      banksQuery.error instanceof Error
        ? banksQuery.error
        : usersBanksFallback.error instanceof Error
          ? usersBanksFallback.error
          : walletTypesQuery.error instanceof Error
            ? walletTypesQuery.error
            : accountsQuery.error instanceof Error
              ? accountsQuery.error
              : null,
    config,
    fields,
  };
}

export interface UseSubmitPaymentResult {
  submit: (input: SubmitPaymentInput) => Promise<WalletPaymentResponse>;
  isSubmitting: boolean;
  error: Error | null;
  result: WalletPaymentResponse | null;
  reset: () => void;
}

export function useSubmitPayment(): UseSubmitPaymentResult {
  const queryClient = useQueryClient();
  const machineGuid = getUserSettings()?.Guid?.trim() ?? '';

  const mutation = useMutation({
    mutationFn: (input: SubmitPaymentInput) => submitPayment(input),
    onSuccess: async (_data, variables) => {
      const historyKey =
        variables.mode === 'deposit'
          ? queryKeys.player.depositHistory({ machineGuid })
          : queryKeys.player.withdrawalHistory({ machineGuid });
      await queryClient.invalidateQueries({ queryKey: historyKey });
    },
  });

  return {
    submit: (input) => mutation.mutateAsync(input),
    isSubmitting: mutation.isPending,
    error: mutation.error instanceof Error ? mutation.error : null,
    result: mutation.data ?? null,
    reset: () => mutation.reset(),
  };
}

/** Soft-limits gate: redirect when account requires limits and user has none. */
export function needsDepositLimitsGate(): boolean {
  const account = getAccount();
  if (!account?.AllowDepositSoftLimits) return false;
  if (account.AllowUserRegistration === false) return false;
  const user = getUserSettings();
  const limits = user?.DepositLimits;
  return limits == null;
}
